import { useState, useEffect, useCallback } from 'react';
import { useSocketStore } from '../stores/socketStore';
import { useAuthStore } from '../stores/authStore';

export interface PremiumBenefits {
  maxRounds: number;
  maxPlayers: number;
  canCreatePrivateRooms: boolean;
  powerUpsPerGame: number;
  customDuration: boolean;
  exclusiveBadges: boolean;
  advancedStats: boolean;
  noAds: boolean;
  exclusiveThemes: boolean;
  prioritySupport: boolean;
}

export interface PremiumStatus {
  isPremium: boolean;
  expiresAt: string | null;
  daysRemaining: number;
  benefits: PremiumBenefits;
  prices?: {
    monthly: number;
    yearly: number;
  };
}

const DEFAULT_FREE_BENEFITS: PremiumBenefits = {
  maxRounds: 20,
  maxPlayers: 4,
  canCreatePrivateRooms: false,
  powerUpsPerGame: 3,
  customDuration: false,
  exclusiveBadges: false,
  advancedStats: false,
  noAds: false,
  exclusiveThemes: false,
  prioritySupport: false,
};

const DEFAULT_PREMIUM_BENEFITS: PremiumBenefits = {
  maxRounds: 100,
  maxPlayers: 100,
  canCreatePrivateRooms: true,
  powerUpsPerGame: -1,
  customDuration: true,
  exclusiveBadges: true,
  advancedStats: true,
  noAds: true,
  exclusiveThemes: true,
  prioritySupport: true,
};

export function usePremium() {
  const { socket } = useSocketStore();
  const { player } = useAuthStore();
  const [status, setStatus] = useState<PremiumStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Charger le statut au montage
  useEffect(() => {
    if (!socket) return;
    
    socket.emit('premium:getStatus');
    
    const handleStatus = (data: PremiumStatus) => {
      setStatus(data);
      setIsLoading(false);
    };
    
    socket.on('premium:status', handleStatus);
    
    return () => {
      socket.off('premium:status', handleStatus);
    };
  }, [socket, player]);
  
  // Rafraîchir le statut
  const refresh = useCallback(() => {
    if (!socket) return;
    setIsLoading(true);
    socket.emit('premium:getStatus');
  }, [socket]);
  
  // Raccourcis
  const isPremium = status?.isPremium ?? false;
  const benefits = status?.benefits ?? DEFAULT_FREE_BENEFITS;
  const daysRemaining = status?.daysRemaining ?? 0;
  const expiresAt = status?.expiresAt ? new Date(status.expiresAt) : null;
  
  // Vérifier si une fonctionnalité est disponible
  const canUse = useCallback((feature: keyof PremiumBenefits): boolean => {
    const value = benefits[feature];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    return false;
  }, [benefits]);
  
  return {
    isPremium,
    benefits,
    daysRemaining,
    expiresAt,
    isLoading,
    status,
    refresh,
    canUse,
    freeBenefits: DEFAULT_FREE_BENEFITS,
    premiumBenefits: DEFAULT_PREMIUM_BENEFITS,
    prices: status?.prices ?? { monthly: 500, yearly: 4000 },
  };
}

// Formater le prix en euros
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

// Liste des avantages pour l'affichage
export const PREMIUM_FEATURES = [
  {
    id: 'privateRooms',
    icon: 'privateRooms',
    title: 'Rooms privées',
    description: 'Créez des parties privées accessibles uniquement par code',
    free: false,
    premium: true,
  },
  {
    id: 'moreRounds',
    icon: 'note',
    title: "Jusqu'à 100 manches",
    description: 'Des parties plus longues avec jusqu\'à 100 chansons',
    free: '10-20 max',
    premium: '100 max',
  },
  {
    id: 'morePlayers',
    icon: 'players',
    title: "Jusqu'à 100 joueurs",
    description: 'Invitez plus d\'amis dans vos parties',
    free: '4 max',
    premium: '100 max',
  },
  {
    id: 'powerUps',
    icon: 'boost',
    title: 'Power-ups illimités',
    description: 'Utilisez autant de power-ups que vous voulez',
    free: '3 / partie',
    premium: 'Illimité',
  },
  {
    id: 'customDuration',
    icon: 'clock',
    title: 'Durée personnalisée',
    description: 'Choisissez la durée d\'écoute de chaque manche',
    free: false,
    premium: true,
  },
  {
    id: 'exclusiveBadges',
    icon: 'award',
    title: 'Badges exclusifs',
    description: 'Débloquez des badges réservés aux membres Premium',
    free: false,
    premium: true,
  },
  {
    id: 'advancedStats',
    icon: 'advancedStats',
    title: 'Stats avancées',
    description: 'Accédez à des statistiques détaillées sur vos performances',
    free: false,
    premium: true,
  },
  {
    id: 'exclusiveThemes',
    icon: 'exclusiveThemes',
    title: 'Thèmes exclusifs',
    description: 'Accédez à des playlists et thèmes réservés aux Premium',
    free: false,
    premium: true,
  },
];

// Badges exclusifs Premium
export interface PremiumBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: 'rare' | 'epic' | 'legendary';
  category: 'achievement' | 'milestone' | 'special' | 'seasonal';
}

export const PREMIUM_BADGES: PremiumBadge[] = [
  // Badges Achievement (exploits)
  {
    id: 'speed_demon',
    name: 'Speed Demon',
    description: 'Répondre en moins de 2 secondes 10 fois',
    icon: 'boost',
    rarity: 'epic',
    category: 'achievement',
  },
  {
    id: 'perfectionist',
    name: 'Perfectionniste',
    description: 'Terminer une partie avec 100% de bonnes réponses',
    icon: 'diamond',
    rarity: 'legendary',
    category: 'achievement',
  },
  {
    id: 'combo_master',
    name: 'Maître du Combo',
    description: 'Atteindre un combo de 15 réponses consécutives',
    icon: 'streak',
    rarity: 'epic',
    category: 'achievement',
  },
  {
    id: 'clutch_king',
    name: 'Clutch King',
    description: 'Gagner une partie avec moins de 100 points d\'avance',
    icon: 'crown',
    rarity: 'legendary',
    category: 'achievement',
  },
  {
    id: 'artist_hunter',
    name: 'Chasseur d\'Artistes',
    description: 'Deviner l\'artiste 50 fois sans le titre',
    icon: 'mic',
    rarity: 'rare',
    category: 'achievement',
  },
  
  // Badges Milestone (jalons)
  {
    id: 'veteran_100',
    name: 'Vétéran',
    description: 'Jouer 100 parties',
    icon: 'award',
    rarity: 'rare',
    category: 'milestone',
  },
  {
    id: 'legend_500',
    name: 'Légende',
    description: 'Jouer 500 parties',
    icon: 'award',
    rarity: 'epic',
    category: 'milestone',
  },
  {
    id: 'immortal_1000',
    name: 'Immortel',
    description: 'Jouer 1000 parties',
    icon: 'infinity',
    rarity: 'legendary',
    category: 'milestone',
  },
  {
    id: 'winner_50',
    name: 'Champion',
    description: 'Gagner 50 parties',
    icon: 'award',
    rarity: 'epic',
    category: 'milestone',
  },
  {
    id: 'dominator',
    name: 'Dominateur',
    description: 'Gagner 10 parties d\'affilée',
    icon: 'streak',
    rarity: 'legendary',
    category: 'milestone',
  },
  
  // Badges Special (spéciaux)
  {
    id: 'early_supporter',
    name: 'Early Supporter',
    description: 'Parmi les premiers membres Premium',
    icon: 'star',
    rarity: 'legendary',
    category: 'special',
  },
  {
    id: 'night_owl',
    name: 'Noctambule',
    description: 'Jouer entre 2h et 5h du matin',
    icon: 'night',
    rarity: 'rare',
    category: 'special',
  },
  {
    id: 'marathon_runner',
    name: 'Marathonien',
    description: 'Jouer 5 heures en une seule session',
    icon: 'clock',
    rarity: 'epic',
    category: 'special',
  },
  {
    id: 'music_guru',
    name: 'Guru Musical',
    description: 'Deviner 1000 titres correctement',
    icon: 'headphones',
    rarity: 'legendary',
    category: 'special',
  },
  {
    id: 'host_master',
    name: 'Maître de Cérémonie',
    description: 'Créer et compléter 25 parties en tant qu\'hôte',
    icon: 'film',
    rarity: 'rare',
    category: 'special',
  },
  
  // Badges Seasonal (saisonniers)
  {
    id: 'winter_2026',
    name: 'Hiver 2026',
    description: 'Badge saisonnier exclusif - Hiver 2026',
    icon: 'freeze',
    rarity: 'epic',
    category: 'seasonal',
  },
  {
    id: 'new_year_2026',
    name: 'Nouvel An 2026',
    description: 'Jouer pendant le réveillon du Nouvel An',
    icon: 'star',
    rarity: 'legendary',
    category: 'seasonal',
  },
  {
    id: 'valentines_2026',
    name: 'Saint Valentin 2026',
    description: 'Jouer le jour de la Saint Valentin',
    icon: 'heart',
    rarity: 'rare',
    category: 'seasonal',
  },
];
